import { FetchCacheItemsSources, FetchItemsGqlQuery } from '../../../../types';
import {
  FetchCacheItemTagsFilters,
  FetchItemTagsCacheKey,
  FetchItemTagsLimit,
  FetchItemTagsPage,
  FetchItemTagsSort
} from '../../itemTagsTypes';
import { InfiniteIndexQueryBaseNodeType } from '../../../common/reactQuery/hooks/useInfiniteIndexQuery';

import { useCacheInfiniteIndexQuery } from '../../../common/reactQuery/hooks/useCacheInfiniteIndexQuery';

import {
  INITIAL_ITEM_TAGS_FILTERS,
  INITIAL_ITEM_TAGS_LIMIT,
  INITIAL_ITEM_TAGS_SORT,
  ItemTagsPermissions
} from '../../itemTagsConstants';

interface CacheItemTagsOptions {
  action?: string;
  cacheKey: FetchItemTagsCacheKey;
  query?: FetchItemsGqlQuery;
  initialFilters?: FetchCacheItemTagsFilters;
  initialSort?: FetchItemTagsSort;
  initialPage?: FetchItemTagsPage;
  initialLimit?: FetchItemTagsLimit;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
  trackTotalHits?: boolean;
  source?: FetchCacheItemsSources;
}

const scope = 'itemTags';

function useCacheItemTags<
  FetchItemTagsItemTagType extends InfiniteIndexQueryBaseNodeType
>({
  action = ItemTagsPermissions.READ_ITEM_TAGS_INDEX_PAGE,
  cacheKey,
  query,
  initialFilters = INITIAL_ITEM_TAGS_FILTERS,
  initialSort = INITIAL_ITEM_TAGS_SORT,
  initialLimit = INITIAL_ITEM_TAGS_LIMIT,
  options = {},
  trackTotalHits,
  source = FetchCacheItemsSources.DB
}: CacheItemTagsOptions) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isFetchingNextPage,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    hasNextPage,
    updateItemCache,
    loadMoreItems,
    filterItems,
    changeItemsFilters,
    clearItemsFilters,
    clearItemsFiltersPersistInitial,
    sortItems,
    limitItems
  } = useCacheInfiniteIndexQuery<FetchItemTagsItemTagType>({
    action,
    defaultApi: 'cache_api',
    scope,
    query,
    cacheQuery: query,
    cacheKey,
    initialFilters,
    initialLimit,
    initialSort,
    options,
    trackTotalHits,
    source
  });

  return {
    itemTagsData: data,
    itemTags: items,
    itemTagsError: itemsError,
    itemTagsTotalCount: itemsTotalCount,
    itemTagsFetched: isFetched,
    itemTagsLoading: isLoading,
    itemTagsFetchingNextPage: isFetchingNextPage,
    itemTagsIsPlaceholderData: isPlaceholderData,
    itemTagsFilters: currentFilters,
    itemTagsSort: currentSort,
    itemTagsPage: currentPage,
    itemTagsLimit: currentLimit,
    hasNextItemTagsPage: hasNextPage,
    updateItemTagCache: updateItemCache,
    loadMoreItemTags: loadMoreItems,
    filterItemTags: filterItems,
    changeItemTagsFilters: changeItemsFilters,
    clearItemTagsFilters: clearItemsFilters,
    clearItemTagsFiltersPersistInitial: clearItemsFiltersPersistInitial,
    sortItemTags: sortItems,
    limitItemTags: limitItems
  };
}

export default useCacheItemTags;
